import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Search, Trash2, ScanLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["all", "identify", "diagnose"];

export default function CollectionScreen() {
  const [scans, setScans] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/scans");
      setScans(data.scans || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const deleteScan = async (id) => {
    try {
      await api.delete(`/scans/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Could not delete"); }
  };

  const filtered = scans
    .filter((s) => (filter === "all" ? true : s.type === filter))
    .filter((s) => {
      if (!q) return true;
      const name = s.result?.species?.[0]?.common_name || s.result?.disease?.name || "";
      return name.toLowerCase().includes(q.toLowerCase());
    });

  return (
    <Shell section="collection">
      <h1 className="text-2xl font-bold mb-4 text-shadow-green">My Collection</h1>
      <div className="glass-card flex items-center gap-3 px-4 py-3 mb-4 fade-up">
        <Search size={18} className="text-[#4CAF50]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search history…"
          className="flex-1 bg-transparent outline-none text-white placeholder-white/30 text-sm"
          data-testid="collection-search"
        />
      </div>
      <div className="flex gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold capitalize transition-all ${
              filter === f ? "bg-[rgba(76,175,80,0.2)] border border-[rgba(76,175,80,0.5)] text-white" : "border border-white/15 text-white/60"
            }`}
            style={filter === f ? { boxShadow: "var(--glow-green)" } : {}}
            data-testid={`collection-filter-${f}`}
          >
            {f === "identify" ? "IDs" : f === "diagnose" ? "Diseases" : "All"}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card p-10 text-center fade-up" data-testid="collection-empty">
          <ScanLine size={28} className="mx-auto text-[#4CAF50] mb-3" />
          <p className="text-soft mb-3">No scans yet.</p>
          <button onClick={() => navigate("/identify")} className="btn-primary">Identify your first plant</button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((s, idx) => {
            const isAd = idx > 0 && idx % 5 === 0 && user?.tier === "free";
            return (
              <React.Fragment key={s.scan_id}>
                {isAd && (
                  <div className="glass-card p-3 text-center text-[11px] text-white/40" style={{ borderColor: "rgba(255,143,0,0.3)" }} data-testid={`collection-ad-${idx}`}>
                    Advertisement
                  </div>
                )}
                <div className={`glass-card flex items-center gap-3 p-3 fade-up stagger-${(idx % 5) + 1}`} data-testid={`collection-item-${idx}`}>
                  <div
                    className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: s.type === "identify" ? "rgba(76,175,80,0.15)" : "rgba(255,143,0,0.15)",
                      border: `1px solid ${s.type === "identify" ? "rgba(76,175,80,0.4)" : "rgba(255,143,0,0.4)"}`,
                    }}
                  >
                    {s.type === "identify" ? <ScanLine size={20} className="text-[#4CAF50]" /> : <ShieldCheck size={20} className="text-[#FF8F00]" />}
                  </div>
                  <button
                    onClick={() => navigate(`/${s.type}/result/${s.scan_id}`)}
                    className="flex-1 text-left"
                  >
                    <p className="text-sm font-semibold truncate">
                      {s.result?.species?.[0]?.common_name || s.result?.disease?.name || "Unknown"}
                    </p>
                    <p className="text-[11px] text-muted-soft">
                      {new Date(s.created_at).toLocaleString()}
                    </p>
                  </button>
                  <span className={`glass-pill ${s.type === "diagnose" ? "amber" : ""}`}>
                    {s.type === "identify" ? "ID" : "Disease"}
                  </span>
                  <button
                    onClick={() => deleteScan(s.scan_id)}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-500/15 transition-colors"
                    data-testid={`collection-delete-${idx}`}
                  >
                    <Trash2 size={14} className="text-white/40 hover:text-red-400" />
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
